import { z } from 'zod'
import { dateOrIsoString } from '~/prix/types/date'
import { lineString } from '~/prix/types/presets/geo'
import sql from '~/prix/utils/sql'
import { clientObject } from '~/packages/general/client/clientObject'

const lineStringPath = lineString().superRefine((geometry, context) => {
  if (geometry.type !== 'LineString') {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'A geometria da estrada complementar deve ser LineString',
    })
    return
  }

  if (!Array.isArray(geometry.coordinates) || geometry.coordinates.length < 2) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'A estrada complementar deve ter ao menos dois pontos',
    })
    return
  }

  geometry.coordinates.forEach((position, positionIndex) => {
    const [longitude, latitude] = position

    if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Longitude inválida na posição ${positionIndex}`,
      })
    }

    if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Latitude inválida na posição ${positionIndex}`,
      })
    }
  })
})

export const complementaryRoadCollectionMethod = z.enum([
  'manualDrawing',
  'vehicleGlobalNavigationSatelliteSystem',
  'topographicSurvey',
  'satelliteImagery',
  'imported',
  'other',
])

export const complementaryRoadObject = z.object({
  id: z.string().uuid(),
  code: z.number().int().nullish(),
  clientId: clientObject.shape.id,
  municipalityId: z.number().int(),
  name: z.string().nullish(),
  alternativeNames: z.array(z.string()).optional().default([]),
  naturalAlternativeNames: z.array(z.string()).optional().default([]),
  point: z.any().nullish(),
  path: lineStringPath,
  reference: z.string().nullish(),
  oldReferences: z.array(z.string()).optional().default([]),
  numberAccuracyPercentage: z.number().nullish(),
  numberPolynomialCoefficients: z.any().nullish(),
  numberAverageAngle: z.any().nullish(),
  highway: z.string().nullish(),
  surface: z.string().nullish(),
  trackType: z.string().nullish(),
  smoothness: z.string().nullish(),
  lanes: z.number().positive().nullish(),
  oneWay: z.boolean().nullish(),
  collectionMethod: complementaryRoadCollectionMethod.nullish(),
  averageGlobalNavigationSatelliteSystemAccuracyMeters: z.number().nonnegative().nullish(),
  collectionMetadata: z.record(z.any()).optional().default({}),
  insertUserId: z.string().nullish(),
  editUserId: z.string().nullish(),
  sourceFilter: z.string().nullish(),
  createdAt: dateOrIsoString().nullish(),
  updatedAt: dateOrIsoString().nullish(),
  synchronizedAt: dateOrIsoString().nullish(),
  deletedAt: dateOrIsoString().nullish(),
})

export const complementaryRoadCreateInput = complementaryRoadObject
  .omit({
    code: true,
    clientId: true,
    municipalityId: true,
    point: true,
    insertUserId: true,
    editUserId: true,
    sourceFilter: true,
    synchronizedAt: true,
    deletedAt: true,
  })
  .extend({
    id: complementaryRoadObject.shape.id.nullish(),
  })

export const complementaryRoadUpdateInput = complementaryRoadCreateInput.partial().extend({
  id: complementaryRoadObject.shape.id,
})

export type ComplementaryRoadType = z.infer<typeof complementaryRoadObject>
export type ComplementaryRoadCreateInputType = z.infer<typeof complementaryRoadCreateInput>
export type ComplementaryRoadUpdateInputType = z.infer<typeof complementaryRoadUpdateInput>

export const propertyMap = {
  id: sql`complementary_road.id`,
  code: sql`complementary_road.code`,
  clientId: sql`complementary_road.client_id`,
  municipalityId: sql`complementary_road.municipality_id`,
  name: sql`complementary_road.name`,
  alternativeNames: sql`complementary_road.alternative_names`,
  naturalAlternativeNames: sql`complementary_road.natural_alternative_names`,
  point: sql`complementary_road.point`,
  path: sql`complementary_road.path`,
  reference: sql`complementary_road.reference`,
  oldReferences: sql`complementary_road.old_references`,
  numberAccuracyPercentage: sql`complementary_road.number_accuracy_percentage`,
  numberPolynomialCoefficients: sql`complementary_road.number_polynomial_coefficients`,
  numberAverageAngle: sql`complementary_road.number_average_angle`,
  highway: sql`complementary_road.highway`,
  surface: sql`complementary_road.surface`,
  trackType: sql`complementary_road.track_type`,
  smoothness: sql`complementary_road.smoothness`,
  lanes: sql`complementary_road.lanes`,
  oneWay: sql`complementary_road.one_way`,
  collectionMethod: sql`complementary_road.collection_method`,
  averageGlobalNavigationSatelliteSystemAccuracyMeters: sql`complementary_road.average_global_navigation_satellite_system_accuracy_meters`,
  collectionMetadata: sql`complementary_road.collection_metadata`,
  insertUserId: sql`complementary_road.insert_user_id`,
  editUserId: sql`complementary_road.edit_user_id`,
  sourceFilter: sql`complementary_road.source_filter`,
  createdAt: sql`complementary_road.created_at`,
  updatedAt: sql`complementary_road.updated_at`,
  synchronizedAt: sql`complementary_road.synchronized_at`,
  deletedAt: sql`complementary_road.deleted_at`,
}
